import { useState } from "react";
import { toast } from "sonner";
import { Contact, Copy, Mail, Settings, UserMinus } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";

export default function MemberContacts({ leagueId, onRemoved }) {
  const [rows, setRows] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [open, setOpen] = useState(false);
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    try {
      const [{ data }, rec] = await Promise.all([api.get(`/superadmin/leagues/${leagueId}/contacts`), api.get(`/leagues/${leagueId}/recruiters`).catch(() => ({ data: { rows: [] } }))]);
      setRows(data); setSponsors(rec.data.rows); setOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Retirer ${r.display_name} de la ligue ? Il ne verra plus la ligue et disparaîtra du classement. Il pourra la rejoindre à nouveau avec une invitation.`)) return;
    try {
      await api.delete(`/superadmin/leagues/${leagueId}/members/${r.user_id}`);
      setRows((x) => x.filter((y) => y.user_id !== r.user_id)); toast.success(`${r.display_name} a été retiré de la ligue`); onRemoved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const copyAll = async () => {
    try { await navigator.clipboard.writeText(rows.map((r) => r.email).join(", ")); toast.success(`${rows.length} adresse(s) copiée(s)`); } catch { toast.error("Copie impossible sur cet appareil"); }
  };
  return (
    <div className="rounded-xl border border-white/10 p-3 text-xs" data-testid={`admin-contacts-${leagueId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={toggle} className="flex items-center gap-2 font-semibold text-white" data-testid={`admin-contacts-toggle-${leagueId}`}>
          <Contact size={14} /> {open ? "Masquer les joueurs" : "Joueurs, e-mails et parrainages"}
        </button>
        <Link to={`/ligues/${leagueId}/parametres`} className="mpl-btn-ghost text-xs" data-testid={`admin-open-settings-${leagueId}`}><Settings size={12} /> Régler les paramètres de la ligue</Link>
      </div>
      {open && rows && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-slate-400">{rows.length} joueur(s) · consultation journalisée, à utiliser uniquement pour contacter les joueurs.</p>
            {rows.length > 0 && <button type="button" onClick={copyAll} className="mpl-btn-ghost text-xs" data-testid={`admin-contacts-copy-${leagueId}`}><Copy size={12} /> Copier toutes les adresses</button>}
          </div>
          {sponsors.length > 0 && (
            <div className="rounded-lg bg-white/[0.03] p-2" data-testid={`admin-recruiters-${leagueId}`}>
              <p className="font-semibold text-white">Classement des parrains</p>
              <ol className="mt-1 space-y-0.5">
                {sponsors.map((s, i) => (
                  <li key={s.user_id} className="flex justify-between gap-2" data-testid={`admin-recruiter-${s.user_id}`}>
                    <span className="truncate">{i + 1}. {s.display_name}{s.username && ` @${s.username}`} {s.top && <span className="ml-1 rounded bg-sky-300/15 px-1.5 text-[10px] font-bold uppercase text-sky-200">Meilleur recruteur</span>}</span>
                    <span className="shrink-0 text-slate-400">{s.count} invité(s) · <span className="text-[#B7F52A]">+{s.points} pts</span></span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <ul className="divide-y divide-white/[0.06]">
            {rows.map((r) => (
              <li key={r.user_id} className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`admin-contact-${r.user_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">{r.display_name} {r.username && <span className="font-normal text-[#B7F52A]">@{r.username}</span>} {r.role === "owner" && <span className="ml-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase text-slate-300">Organisateur</span>}</p>
                  <p className="truncate text-slate-400">{r.email}</p>
                  {r.invited_by && <p className="truncate text-[11px] text-slate-500" data-testid={`admin-contact-invited-by-${r.user_id}`}>Invité par {r.invited_by.label}{r.invited_by.username && ` @${r.invited_by.username}`}{r.new_account && rows.some((x) => x.user_id === r.invited_by.user_id) && " · nouveau compte (+100 pts au parrain)"}</p>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <a href={`mailto:${r.email}`} className="mpl-btn-ghost text-xs" data-testid={`admin-contact-mail-${r.user_id}`}><Mail size={12} /> Écrire</a>
                  {r.role !== "owner" && <button type="button" onClick={() => remove(r)} className="mpl-btn-ghost text-xs text-red-300" data-testid={`admin-contact-remove-${r.user_id}`}><UserMinus size={12} /> Retirer</button>}
                </div>
              </li>
            ))}
            {!rows.length && <li className="py-2 text-slate-400">Aucun joueur pour l'instant.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
